import React, { useEffect, useRef, useState } from "react";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

const Plaque = ({ formData, onChange, disabled }) => {
  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...formData },
    touched: {},
    errors: {},
  });

  const formatValue = (value) => {
    if (value) {
      return value
        .replace(/[^\d]/g, "") // فقط اعداد را می‌پذیرد
        .replace(/(\d{3})(\d)/, "$1-$2") // فرمت شماره
        .replace(/-(\d{3})(\d)/, "-$1-$2") // فرمت شماره
        .replace(/-(\d{4})/, "-$1"); // فرمت شماره
    }
    return value;
  };

  const hasError = (index) =>
    formState.touched[index] && formState.errors[index] ? true : false;

  const handleChange = (event, index, regex = /^[0-9]*$/) => {
    const { value } = event.target;

    if (!regex.test(value)) {
      return setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          [index]: undefined,
        },
        errors: {
          ...formState.errors,
          [index]: true,
        },
      }));
    }

    if (regex.test(value)) {
      value && focusNextInput(index, value);
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          [index]: value || "",
        },
        touched: {
          ...formState.touched,
          [index]: true,
        },
        errors: {
          ...formState.errors,
          [index]: false,
        },
      }));
    }
  };

  const inputRefs = [
    { id: 0, ref: useRef(null), type: "text" },
    { id: 1, ref: useRef(null), type: "text" },
    { id: 2, ref: useRef(null), type: "select" },
    { id: 3, ref: useRef(null), type: "text" },
    { id: 4, ref: useRef(null), type: "text" },
    { id: 5, ref: useRef(null), type: "text" },
    { id: 6, ref: useRef(null), type: "text" },
    { id: 7, ref: useRef(null), type: "text" },
  ];

  const focusNextInput = (index) => {
    if (index < inputRefs.length - 1) {
      return inputRefs[index + 1].ref.current.focus();
    }
  };

  const focusPrevInput = (index) => {
    if (index > 0 && index <= inputRefs.length - 1) {
      return inputRefs[index - 1].ref.current.focus();
    }
  };

  const handleKeyDown = (event, index, type) => {
    if (event.key === "ArrowLeft" && index > 0) {
      return focusPrevInput(index);
    }
    if (
      (event.key === "ArrowRight" || event.key === "Enter") &&
      index < inputRefs.length - 1
    ) {
      return focusNextInput(index);
    }
    if (event.key === "Delete" || event.key === "Backspace") {
      if (type === "select") {
        setFormState((formState) => ({
          ...formState,
          values: {
            ...formState.values,
            [index]: undefined,
          },
          touched: {
            ...formState.touched,
            [index]: true,
          },
          errors: {
            ...formState.errors,
            [index]: false,
          },
        }));
        return focusPrevInput(index);
      }
      if (inputRefs[index].ref.current.value.length === 0) {
        return focusPrevInput(index);
      }
    }
    return;
  };

  useEffect(() => {
    const plate_letters = formState.values[2] || undefined;
    const plate_serial_number =
      formState.values[6] && formState.values[7]
        ? `${formState.values[6]}${formState.values[7]}`
        : undefined;
    const two_left_digits_plate =
      formState.values[0] && formState.values[1]
        ? `${formState.values[0]}${formState.values[1]}`
        : undefined;
    const three_right_digits_plate =
      formState.values[3] && formState.values[4] && formState.values[5]
        ? `${formState.values[3]}${formState.values[4]}${formState.values[5]}`
        : undefined;
    onChange({
      plate_letters,
      plate_serial_number,
      two_left_digits_plate,
      three_right_digits_plate,
    });
  }, [formState.values]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Stack
        height="100%"
        direction="row"
        alignItems="stretch"
        sx={{
          direction: "rtl",
        }}
      >
        <Stack
          p={0.5}
          flex={1}
          rowGap={0.5}
          bgcolor="blue"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <img
            src="/assets/icons/fa.webp"
            style={{ width: 32, height: 16, objectFit: "contain" }}
          />
          <Stack>
            <Typography color="#fff" variant="subtitle1" fontSize={8}>
              I.R.
            </Typography>
            <Typography
              color="#fff"
              align="justify"
              variant="subtitle1"
              fontSize={8}
              letterSpacing={1}
            >
              IRAN
            </Typography>
          </Stack>
        </Stack>
        <Stack
          py={1}
          px={0.5}
          flex={2}
          columnGap={0.2}
          direction="row"
          alignItems="center"
          bgcolor="background.paper"
        >
          {inputRefs.slice(0, 2).map(({ id, ref, type }) => {
            return (
              <TextField
                key={id}
                type={type}
                fullWidth
                inputRef={ref}
                size="medium"
                variant="outlined"
                onChange={(event) => handleChange(event, id)}
                onKeyDown={(event) => handleKeyDown(event, id)}
                value={formatValue(formState.values[id]) || ""}
                inputProps={{
                  maxLength: 1,
                  style: {
                    fontSize: 16,
                    paddingLeft: 1,
                    paddingRight: 1,
                    textAlign: "center",
                    fontWeight: "bold",
                  },
                }}
                error={hasError(id)}
              />
            );
          })}
        </Stack>
        <Stack
          py={1}
          // px={0.5}
          flex={1}
          columnGap={0.2}
          direction="row"
          alignItems="center"
          bgcolor="background.paper"
        >
          {inputRefs.slice(2, 3).map(({ id, ref, type }) => {
            if (type === "select") {
              return (
                <TextField
                  key={id}
                  select
                  fullWidth
                  inputRef={ref}
                  size="medium"
                  variant="outlined"
                  error={hasError(id)}
                  value={formState.values[id] || ""}
                  onKeyDown={(event) => handleKeyDown(event, id, type)}
                  onChange={(event) => handleChange(event, id, /^[a-zA-Z]+$/)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      fontSize: 16,
                      paddingLeft: 1,
                      paddingRight: 1,
                      textAlign: "center",
                      fontWeight: "bold",
                    },
                  }}
                  SelectProps={{
                    sx: {
                      p: 0,
                      fontSize: 16,
                      paddingLeft: 1,
                      paddingRight: 1,
                      direction: "rtl",
                      fontWeight: "bold",
                    },
                  }}
                >
                  <MenuItem
                    // sx={{
                    //   p:1,
                    //   fontSize: 16,
                    //   direction: "ltr",
                    //   textAlign: "center",
                    //   fontWeight: "bold",
                    // }}
                    value="a"
                  >
                    ع
                  </MenuItem>
                  <MenuItem
                    // sx={{
                    //   p:1,
                    //   fontSize: 16,
                    //   direction: "ltr",
                    //   textAlign: "center",
                    //   fontWeight: "bold",
                    // }}
                    value="f"
                  >
                    ف
                  </MenuItem>
                  <MenuItem
                    // sx={{
                    //   p:1,
                    //   fontSize: 16,
                    //   direction: "ltr",
                    //   textAlign: "center",
                    //   fontWeight: "bold",
                    // }}
                    value="p"
                  >
                    پ
                  </MenuItem>
                </TextField>
              );
            }
            return (
              <TextField
                key={id}
                type={type}
                fullWidth
                inputRef={ref}
                size="medium"
                variant="outlined"
                onChange={(event) => handleChange(event, id)}
                onKeyDown={(event) => handleKeyDown(event, id)}
                value={formatValue(formState.values[id]) || ""}
                inputProps={{
                  maxLength: 1,
                  style: {
                    fontSize: 16,
                    paddingLeft: 1,
                    paddingRight: 1,
                    textAlign: "center",
                    fontWeight: "bold",
                  },
                }}
                error={hasError(id)}
              />
            );
          })}
        </Stack>
        <Stack
          py={1}
          px={0.5}
          flex={3}
          columnGap={0.2}
          direction="row"
          alignItems="center"
          bgcolor="background.paper"
        >
          {inputRefs.slice(3, 6).map(({ id, ref, type }) => {
            return (
              <TextField
                key={id}
                type={type}
                fullWidth
                inputRef={ref}
                size="medium"
                variant="outlined"
                onChange={(event) => handleChange(event, id)}
                onKeyDown={(event) => handleKeyDown(event, id)}
                value={formatValue(formState.values[id]) || ""}
                inputProps={{
                  maxLength: 1,
                  style: {
                    fontSize: 16,
                    paddingLeft: 1,
                    paddingRight: 1,
                    textAlign: "center",
                    fontWeight: "bold",
                  },
                }}
                error={hasError(id)}
              />
            );
          })}
        </Stack>
        <Stack
          py={1}
          px={0.5}
          flex={2}
          columnGap={0.2}
          alignItems="center"
          bgcolor="action.hover"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" align="justify">
            ایـــــران
          </Typography>
          <Stack columnGap={0.2} direction="row" alignItems="center">
            {inputRefs.slice(6, 8).map(({ id, ref, type }) => {
              return (
                <TextField
                  key={id}
                  type={type}
                  fullWidth
                  inputRef={ref}
                  size="small"
                  variant="outlined"
                  onChange={(event) => handleChange(event, id)}
                  onKeyDown={(event) => handleKeyDown(event, id)}
                  value={formatValue(formState.values[id]) || ""}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      fontSize: 16,
                      paddingLeft: 1,
                      paddingRight: 1,
                      textAlign: "center",
                      fontWeight: "bold",
                    },
                  }}
                  error={hasError(id)}
                />
              );
            })}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

export default Plaque;
